import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError, TouchscreenApiClient, type VenueSearchResult } from "@touchscreen/api-client";
import { touchscreenKeys } from "./queryKeys.js";

/**
 * Free-text venue search for the phone entry screen — runs before any session exists, so unlike
 * every other hook here it doesn't read `useTouchscreenContext()` and instead owns a bare,
 * unauthenticated client of its own. `GET /venues/search` is deliberately constrained server-side
 * (`422 query_too_short` below `minLength`, small result cap, heavy rate limiting) — mirror the
 * minimum client-side so a punter mid-word isn't firing a request per keystroke that's guaranteed
 * to 422. Pass the already-debounced query in, same convention as `useSearch`.
 */
export function useVenueSearch(
  baseUrl: string,
  debouncedQuery: string,
  options?: { enabled?: boolean; minLength?: number },
): UseQueryResult<VenueSearchResult[], ApiError> {
  const trimmed = debouncedQuery.trim();
  const minLength = options?.minLength ?? 5;
  // Gate on `baseUrl` too — the client isn't constructed until `queryFn` actually runs, so an
  // unset/empty baseUrl (e.g. a dev checkout missing its `.env`) never gets that far and skips
  // silently rather than throwing on every render.
  const enabled = (options?.enabled ?? true) && !!baseUrl && trimmed.length >= minLength;

  return useQuery({
    queryKey: touchscreenKeys.venueSearch(trimmed),
    queryFn: () => new TouchscreenApiClient({ baseUrl, surface: "phone" }).venuesSearch(trimmed),
    enabled,
  });
}
